import { z } from 'zod';
import { isoDateSchema } from './people.js';
import { slugSchema } from './tenancy.js';

export const EVENT_STATUS = ['DRAFT', 'PUBLISHED', 'IN_PROGRESS', 'DONE', 'CANCELLED'] as const;
export const eventStatusSchema = z.enum(EVENT_STATUS);
export type EventStatus = z.infer<typeof eventStatusSchema>;

export const EVENT_STATUS_LABEL: Record<EventStatus, string> = {
  DRAFT: 'Rascunho',
  PUBLISHED: 'Publicado',
  IN_PROGRESS: 'Em andamento',
  DONE: 'Realizado',
  CANCELLED: 'Cancelado',
};

export const RECURRENCE_FREQUENCY = [
  'WEEKLY',
  'BIWEEKLY',
  'MONTHLY_DAY',
  'MONTHLY_WEEKDAY',
] as const;
export const recurrenceFrequencySchema = z.enum(RECURRENCE_FREQUENCY);
export type RecurrenceFrequency = z.infer<typeof recurrenceFrequencySchema>;

export const RECURRENCE_FREQUENCY_LABEL: Record<RecurrenceFrequency, string> = {
  WEEKLY: 'Toda semana',
  BIWEEKLY: 'A cada duas semanas',
  MONTHLY_DAY: 'Todo mês, em um dia fixo',
  MONTHLY_WEEKDAY: 'Todo mês, no mesmo dia da semana',
};

export const ORDINAL_LABEL: Record<string, string> = {
  '1': 'primeiro',
  '2': 'segundo',
  '3': 'terceiro',
  '4': 'quarto',
  '-1': 'último',
};

/** Hora no formato 24h, como a igreja escreve no cartaz: 18:00, 19:30. */
export const horaSchema = z
  .string()
  .trim()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use o formato 24h, ex.: 19:00');

const corSchema = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Informe a cor em hexadecimal, ex.: #4F46E5');

// ─────────────────────────────────────────────────────────────────────────────
// Tipos de evento
// ─────────────────────────────────────────────────────────────────────────────

export const createEventTypeSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome do tipo').max(80),
  slug: slugSchema.optional(),
  color: corSchema.default('#4F46E5'),
  icon: z.string().trim().max(40).default('calendar'),
  defaultDurationMinutes: z.coerce.number().int().min(15).max(1440).default(120),
  defaultStartTime: horaSchema.default('19:00'),
  requiresTeam: z.boolean().default(true),
});
export type CreateEventTypeInput = z.infer<typeof createEventTypeSchema>;

export const updateEventTypeSchema = createEventTypeSchema.partial().extend({
  active: z.boolean().optional(),
});
export type UpdateEventTypeInput = z.infer<typeof updateEventTypeSchema>;

export const eventTypeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  color: z.string(),
  icon: z.string(),
  defaultDurationMinutes: z.number().int(),
  defaultStartTime: z.string(),
  requiresTeam: z.boolean(),
  isSystem: z.boolean(),
  active: z.boolean(),
  eventsCount: z.number().int(),
});
export type EventType = z.infer<typeof eventTypeSchema>;

/** Tipos que quase toda igreja protestante brasileira usa. */
export const EVENT_TYPE_PRESETS: ReadonlyArray<{
  name: string;
  color: string;
  icon: string;
  duration: number;
  startTime: string;
  requiresTeam?: boolean;
}> = [
  { name: 'Culto de celebração', color: '#4F46E5', icon: 'church', duration: 120, startTime: '19:00' },
  { name: 'Culto da manhã', color: '#0EA5E9', icon: 'sunrise', duration: 120, startTime: '09:00' },
  { name: 'Culto de oração', color: '#7C3AED', icon: 'hands', duration: 90, startTime: '19:30' },
  { name: 'Escola Bíblica Dominical', color: '#059669', icon: 'book-open', duration: 90, startTime: '09:00' },
  { name: 'Ensaio', color: '#F59E0B', icon: 'music', duration: 120, startTime: '20:00' },
  { name: 'Vigília', color: '#1F2937', icon: 'moon', duration: 240, startTime: '22:00' },
  { name: 'Congresso', color: '#DC2626', icon: 'users', duration: 180, startTime: '19:00' },
  { name: 'Batismo', color: '#0891B2', icon: 'droplet', duration: 120, startTime: '16:00' },
  { name: 'Reunião de liderança', color: '#667085', icon: 'clipboard', duration: 90, startTime: '20:00', requiresTeam: false },
];

// ─────────────────────────────────────────────────────────────────────────────
// Eventos
// ─────────────────────────────────────────────────────────────────────────────

export const recurrenceSchema = z
  .object({
    frequency: recurrenceFrequencySchema,
    weekdays: z.array(z.number().int().min(0).max(6)).max(7).default([]),
    monthDay: z.coerce.number().int().min(1).max(31).nullish(),
    ordinal: z.coerce.number().int().min(-1).max(4).nullish(),
    weekday: z.coerce.number().int().min(0).max(6).nullish(),
    endsOn: isoDateSchema.nullish(),
    exceptions: z.array(isoDateSchema).max(60).default([]),
  })
  .superRefine((dados, ctx) => {
    if (
      (dados.frequency === 'WEEKLY' || dados.frequency === 'BIWEEKLY') &&
      dados.weekdays.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['weekdays'],
        message: 'Escolha pelo menos um dia da semana',
      });
    }

    if (dados.frequency === 'MONTHLY_DAY' && !dados.monthDay) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['monthDay'],
        message: 'Informe o dia do mês',
      });
    }

    if (dados.frequency === 'MONTHLY_WEEKDAY' && (dados.ordinal == null || dados.weekday == null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ordinal'],
        message: 'Escolha qual semana e qual dia, ex.: primeiro domingo',
      });
    }

    if (dados.ordinal === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ordinal'],
        message: 'Use 1 a 4, ou último',
      });
    }
  });
export type RecurrenceInput = z.infer<typeof recurrenceSchema>;

export const createEventSchema = z.object({
  title: z.string().trim().min(3, 'Informe o título do evento').max(160),
  eventTypeId: z.string().uuid('Escolha o tipo de evento'),
  congregationId: z.string().uuid().nullish(),
  date: isoDateSchema,
  startTime: horaSchema,
  durationMinutes: z.coerce.number().int().min(15).max(1440).default(120),
  description: z.string().trim().max(1000).nullish(),
  location: z.string().trim().max(240).nullish(),
  notes: z.string().trim().max(1000).nullish(),
  isPublic: z.boolean().default(true),
  /** Ensaio ligado ao culto: herda a equipe (D21). */
  parentEventId: z.string().uuid().nullish(),
  /** Quando presente, cria a série inteira em vez de um evento só. */
  recurrence: recurrenceSchema.nullish(),
  /** Até quando materializar as ocorrências. Padrão: 6 meses. */
  horizonMonths: z.coerce.number().int().min(1).max(24).default(6),
  /** Vagas de equipe já criadas junto com o evento. */
  positions: z
    .array(
      z.object({
        functionId: z.string().uuid(),
        quantity: z.coerce.number().int().min(1).max(200).default(1),
        essential: z.boolean().default(true),
        notes: z.string().trim().max(300).nullish(),
      }),
    )
    .max(80)
    .default([]),
});
export type CreateEventInput = z.infer<typeof createEventSchema>;

export const updateEventSchema = z.object({
  title: z.string().trim().min(3).max(160).optional(),
  date: isoDateSchema.optional(),
  startTime: horaSchema.optional(),
  durationMinutes: z.coerce.number().int().min(15).max(1440).optional(),
  description: z.string().trim().max(1000).nullish(),
  location: z.string().trim().max(240).nullish(),
  notes: z.string().trim().max(1000).nullish(),
  isPublic: z.boolean().optional(),
  status: eventStatusSchema.optional(),
  congregationId: z.string().uuid().nullish(),
});
export type UpdateEventInput = z.infer<typeof updateEventSchema>;

/** Alterar uma ocorrência não altera a série — a menos que a pessoa peça. */
export const SERIES_SCOPE = ['ONLY_THIS', 'THIS_AND_FUTURE', 'ALL'] as const;
export const seriesScopeSchema = z.enum(SERIES_SCOPE);
export type SeriesScope = z.infer<typeof seriesScopeSchema>;

export const SERIES_SCOPE_LABEL: Record<SeriesScope, string> = {
  ONLY_THIS: 'Só este evento',
  THIS_AND_FUTURE: 'Este e os próximos',
  ALL: 'Todos da série',
};

export const cancelEventSchema = z.object({
  reason: z.string().trim().min(3, 'Diga o motivo — quem está escalado precisa saber').max(300),
  scope: seriesScopeSchema.default('ONLY_THIS'),
});
export type CancelEventInput = z.infer<typeof cancelEventSchema>;

export const coverageSchema = z.object({
  necessario: z.number().int(),
  escalados: z.number().int(),
  confirmados: z.number().int(),
  abertas: z.number().int(),
  abertasEssenciais: z.number().int(),
  sinal: z.enum(['aberta', 'aguardando', 'confirmada']),
  percentual: z.number().int(),
  resumo: z.string(),
});
export type Coverage = z.infer<typeof coverageSchema>;

export const eventPositionSchema = z.object({
  id: z.string().uuid(),
  functionId: z.string().uuid(),
  functionName: z.string(),
  ministryId: z.string().uuid(),
  ministryName: z.string(),
  ministryColor: z.string(),
  quantity: z.number().int(),
  essential: z.boolean(),
  notes: z.string().nullable(),
  filled: z.number().int(),
  confirmed: z.number().int(),
  sinal: z.enum(['aberta', 'aguardando', 'confirmada']),
});
export type EventPosition = z.infer<typeof eventPositionSchema>;

export const eventSummarySchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  status: eventStatusSchema,
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  /** Data e hora já no fuso da igreja, prontas para a tela. */
  localDate: z.string(),
  localTime: z.string(),
  weekday: z.number().int(),
  eventType: z.object({ id: z.string(), name: z.string(), color: z.string(), icon: z.string() }),
  congregation: z.object({ id: z.string(), name: z.string() }).nullable(),
  seriesId: z.string().uuid().nullable(),
  parentEventId: z.string().uuid().nullable(),
  coverage: coverageSchema,
});
export type EventSummary = z.infer<typeof eventSummarySchema>;

export const eventDetailSchema = eventSummarySchema.extend({
  description: z.string().nullable(),
  location: z.string().nullable(),
  notes: z.string().nullable(),
  isPublic: z.boolean(),
  detached: z.boolean(),
  cancelledReason: z.string().nullable(),
  durationMinutes: z.number().int(),
  positions: z.array(eventPositionSchema),
  series: z
    .object({ id: z.string(), description: z.string(), active: z.boolean() })
    .nullable(),
  children: z.array(eventSummarySchema),
});
export type EventDetail = z.infer<typeof eventDetailSchema>;

export const eventsQuerySchema = z.object({
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  eventTypeId: z.string().uuid().optional(),
  congregationId: z.string().uuid().optional(),
  status: eventStatusSchema.optional(),
  seriesId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type EventsQuery = z.infer<typeof eventsQuerySchema>;

export const setPositionsSchema = z.object({
  positions: z
    .array(
      z.object({
        functionId: z.string().uuid(),
        quantity: z.coerce.number().int().min(1).max(200),
        essential: z.boolean().default(true),
        notes: z.string().trim().max(300).nullish(),
      }),
    )
    .max(80),
  /** Aplica as mesmas vagas às próximas ocorrências da série. */
  scope: seriesScopeSchema.default('ONLY_THIS'),
});
export type SetPositionsInput = z.infer<typeof setPositionsSchema>;

export const seriesSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  description: z.string(),
  frequency: recurrenceFrequencySchema,
  startTime: z.string(),
  durationMinutes: z.number().int(),
  startsOn: z.string(),
  endsOn: z.string().nullable(),
  generatedUntil: z.string().nullable(),
  active: z.boolean(),
  eventType: z.object({ id: z.string(), name: z.string(), color: z.string() }),
  occurrences: z.number().int(),
  nextOccurrence: z.string().datetime().nullable(),
});
export type Series = z.infer<typeof seriesSchema>;
